import React, { useState } from 'react';
import { Box, IconButton } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { ChartOptions } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { Props } from './types';
import NoDataComponent from '../NoDataAvailable';

const ITEMS_PER_PAGE = 8;

const getChartOptions = ({
  title,
  showSales = true,
  isStacked = false,
  showDataLabels = true,
  min,
  max,
}: {
  title: string;
  showSales?: boolean;
  isStacked?: boolean;
  showDataLabels?: boolean;
  min: number;
  max: number;
}): ChartOptions<'bar' | 'line'> => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'top',
      labels: {
        color: 'black',
        font: {
          size: 13,
          weight: 'bold',
        },
      },
    },
    title: {
      display: !!title,
      text: title,
      color: '#2e6acf',
      font: {
        size: 20,
        weight: 'bold',
      },
    },
    datalabels: showDataLabels
      ? {
          color: 'white',
          font: {
            size: 12,
            weight: 'bold',
          },
          formatter: (value, context) => {
            const label = context.dataset.label;
            return value > 0 ? `${value} ${label?.[0]}` : null;
          },
          anchor: 'center',
          align: 'center',
        }
      : undefined,
    tooltip: {
      callbacks: {
        label: (tooltipItem) => {
          const { chart, dataIndex } = tooltipItem;
          const datasets = chart.data.datasets;
          const { footfallCount, salesCount } = datasets.reduce(
            (acc, dataset) => {
              const value = Number(dataset.data[dataIndex]) ?? 0;
              if (dataset.label === 'Footfall') acc.footfallCount = value;
              if (dataset.label === 'Sales') acc.salesCount = value;
              return acc;
            },
            { footfallCount: 0, salesCount: 0 },
          );

          return [
            `Footfall Count: ${footfallCount}`,
            showSales ? `Sales Count: ${salesCount}` : '',
          ];
        },
      },
    },
  },

  scales: {
    x: {
      stacked: isStacked,
      ticks: {
        color: 'black',
        font: {
          weight: 'bold',
        },
      },
      min,
      max,
    },
    y: {
      stacked: isStacked,
      ticks: {
        color: 'black',
        font: {
          weight: 'bold',
        },
      },
    },
  },
});

const LineBarChart = ({ chartData, title, showSales }: Props) => {
  const [pageIndex, setPageIndex] = useState(0);
  const totalBars = chartData.labels?.length ?? 0;
  const maxPage = Math.ceil(totalBars / ITEMS_PER_PAGE) - 1;

  const min = pageIndex * ITEMS_PER_PAGE;
  const max = min + ITEMS_PER_PAGE - 1;

  const options = getChartOptions({ title, showSales, min, max });

  const handleNext = () => {
    if (pageIndex < maxPage) {
      setPageIndex(pageIndex + 1);
    }
  };

  const handlePrev = () => {
    if (pageIndex > 0) {
      setPageIndex(pageIndex - 1);
    }
  };

  return (
    <Box display='flex' flexDirection='column' height='100%' justifyContent='center'>
      {chartData.labels?.length === 0 ? (
        <NoDataComponent title={title} />
      ) : (
        <>
          <Box display='flex' alignItems='center' justifyContent='center' height={'100%'}>
            <IconButton onClick={handlePrev} disabled={pageIndex === 0}>
              <ChevronLeft />
            </IconButton>

            <Box flex={1} maxWidth='calc(100% - 100px)' height={'100%'}>
              <Chart plugins={[ChartDataLabels]} type='bar' data={chartData} options={options} />
            </Box>
            <IconButton onClick={handleNext} disabled={pageIndex === maxPage}>
              <ChevronRight />
            </IconButton>
          </Box>
        </>
      )}
    </Box>
  );
};

export default LineBarChart;
